<script setup lang="ts">
import type { NuxtError } from '#app'

defineProps({
  error: {
    type: Object as PropType<NuxtError>,
    required: true,
  },
})

const email = ref('')
const loading = ref(false)
const honeypot = ref('')
const toast = useToast()
const router = useRouter()

async function navigateToForm() {
  if (honeypot.value) {
    console.log('Bot detected')
    return
  }

  if (!email.value) {
    toast.add({
      title: 'Error',
      description: 'Please enter your email',
      color: 'red',
    })
    return
  }

  loading.value = true

  try {
    const response = await $fetch('/api/subscribe', {
      method: 'POST',
      body: {
        email: email.value,
        timestamp: Date.now(),
      },
    })

    if (response.success) {
      router.push({
        path: '/subscribe',
        query: {
          email: email.value,
          category: 'aerospace',
          isMain: 'true',
        },
      })
    }
  } catch (error) {
    loading.value = false
    toast.add({
      title: 'Error',
      description: error.data?.message || 'Failed to subscribe',
      color: 'red',
    })
  }
}

// Get runtime config for site URL
const config = useRuntimeConfig()

// Override the title template completely to avoid inheritance issues
useHead({
  titleTemplate: 'AERO.NOW • Page Not Found',
  title: null, // Set title to null when using titleTemplate directly
  htmlAttrs: {
    lang: 'en',
  },
  meta: [
    {
      name: 'description',
      content: 'We are sorry but this page could not be found.',
    },
    {
      property: 'og:title',
      content: 'AERO.NOW • Page Not Found',
    },
    {
      property: 'og:description',
      content: 'We are sorry but this page could not be found.',
    },
    {
      property: 'og:image',
      content: `${config.public.siteUrl}/social-card.png`,
    },
    {
      name: 'twitter:title',
      content: 'AERO.NOW • Page Not Found',
    },
    {
      name: 'twitter:description',
      content: 'We are sorry but this page could not be found.',
    },
    {
      name: 'twitter:image',
      content: `${config.public.siteUrl}/social-card.png`,
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
  ],
})
</script>

<template>
  <NuxtLayout name="default">
    <div class="error-page-container">
      <UContainer>
        <UPage class="error-page">
          <!-- Custom error layout -->
          <div class="error-content">
            <h1 class="text-5xl font-bold mb-4">
              {{
                error.statusCode === 404
                  ? 'Page not found'
                  : 'An error occurred'
              }}
            </h1>

            <!-- Enhanced Newsletter Signup -->
            <ULandingSection class="w-full max-w-md mx-auto py-0 pt-8 sm:py-0">
              <template #description>
                <p class="text-lg mb-4 text-gray-700 dark:text-white">
                  While you're here, get aerospace insights in just
                  <strong>5 minutes per day 🚀</strong>
                </p>
              </template>
              <template #headline>
                <div
                  class="bg-gray-50/50 dark:bg-gray-900/30 rounded-xl p-6 shadow-sm backdrop-blur-sm mt-4"
                >
                  <form
                    class="max-w-lg mx-auto"
                    @submit.prevent="navigateToForm"
                  >
                    <UFormGroup :ui="{ container: 'mt-3' }">
                      <UInput
                        v-model="email"
                        type="email"
                        placeholder="Enter your email"
                        :ui="{ icon: { trailing: { pointer: '' } } }"
                        required
                        size="xl"
                        autocomplete="off"
                        autofocus
                        class="w-full"
                        input-class="rounded-full w-[300px] sm:w-[350px] border-2 border-primary focus:ring-primary focus:border-primary focus:outline-none text-truncate pr-[125px]"
                      >
                        <template #trailing>
                          <UButton
                            type="submit"
                            size="xs"
                            color="primary"
                            :label="loading ? 'Subscribing' : 'Subscribe Free'"
                            :loading="loading"
                          />
                        </template>
                      </UInput>
                    </UFormGroup>
                    <input
                      v-model="honeypot"
                      type="text"
                      name="contact-preference"
                      aria-hidden="true"
                      class="absolute top-0 left-0 h-0 w-0 -z-50 opacity-0 pointer-events-none"
                      tabindex="-1"
                      autocomplete="off"
                    />
                  </form>

                  <div
                    class="mt-3 flex items-center justify-center gap-1 text-xs text-gray-500"
                  >
                    <UIcon
                      name="i-heroicons-shield-check"
                      class="text-gray-400 translate-y-[-1px]"
                    />
                    <p>No spam. Unsubscribe instantly anytime.</p>
                  </div>
                </div>
              </template>
            </ULandingSection>

            <div class="flex justify-center w-full items-center mt-8">
              <UButton
                to="/"
                label="Still not convinced?"
                color="black"
                size="sm"
                variant="ghost"
              >
                <template #leading>
                  <UIcon
                    name="i-heroicons-arrow-left-20-solid"
                    class="w-5 h-5"
                  />
                </template>
              </UButton>
            </div>
          </div>
        </UPage>
      </UContainer>
    </div>
  </NuxtLayout>
</template>

<style scoped>
.error-page-container {
  margin-top: 50px !important; /* Reduced from 100px */
  padding-top: 20px !important; /* Reduced from 50px */
}

.error-page {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 65vh !important;
  padding-top: 2rem !important; /* Reduced from 4rem */
  padding-bottom: 3rem !important; /* 16px * 3 = 48px bottom padding */
}

.error-content {
  width: 100% !important;
  max-width: 42rem !important; /* 2xl */
  text-align: center !important;
  margin-left: auto !important;
  margin-right: auto !important;
  padding-top: 1rem !important; /* Reduced from 1.5rem */
}
</style>
